import PhotoPlaceholderDoctor from 'assets/images/placeholder-doctor.png';
import PhotoPlaceholderPatient from 'assets/images/placeholder-upload.jpg';
import { toast } from 'components/Toast';
import {
  differenceInYears,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { saveAs } from 'file-saver';
import { API_URL } from 'utils/config';
import { GENDER_OPTIONS } from './constants';
import { IFileCheckResponse, ISelectOptions } from './models';
import { IDoctorDetails, IDoctorResponse } from './models/doctor';
import {
  IPatientBackground,
  IPatientDetails,
  IPatientHistory,
  IPatientResponse
} from './models/patient';
import { TCalendarView, TGenders } from './types';

export const range = (start: number, end: number) =>
  Array.from(Array(Math.abs(end - start) + 1), (_, i) => start + i);

const genders: any = {
  MALE: 'Male',
  FEMALE: 'Female',
  NOTTOSAY: 'Preferred not to say'
};

export const getConditions = (user: IPatientDetails | null) => {
  let conditions = '-';

  if (user?.profile) {
    let arrConditions: string[] = [];
    if (user.profile.conditions) {
      arrConditions = JSON.parse(user.profile.conditions);
    }

    if (user.profile.other_conditions) arrConditions.push(user.profile.other_conditions);
    conditions = arrConditions.join(', ');
  }

  return conditions;
};

// export const getConditions = (user: IPatientDetails | null) => {
//   let conditions = '-';

//   if (user) {
//     const arrConditions: string[] = [];
//     if (user.profile.conditions) {
//       arrConditions.push(JSON.parse(user.profile.conditions));
//     }

//     arrConditions.push(user.profile.other_conditions);
//     conditions = arrConditions.join(', ');
//   }

//   return conditions;
// };

export const getDobAge = (dateOfBirth: Date, formatDob?: string) => {
  const age = differenceInYears(new Date(), new Date(dateOfBirth));

  return {
    dob: format(new Date(dateOfBirth), formatDob || 'dd/MM/yyyy'),
    age,
    combined: `${format(new Date(dateOfBirth), formatDob || 'dd/MM/yyyy')} - ${age}`,
    dobAge: `${format(new Date(dateOfBirth), formatDob || 'dd/MM/yyyy')} - ${age} years old`
  };
};

export const getGenderValue = (gender: TGenders | undefined, type: 'select' | 'input') => {
  let genderValue: any = gender
    ? !(gender === 'FEMALE' || gender === 'MALE' || gender === 'NOTTOSAY')
      ? 'OTHERS'
      : gender
    : '';

  if (type === 'input') {
    genderValue =
      gender && !(gender === 'FEMALE' || gender === 'MALE' || gender === 'NOTTOSAY') ? gender : '';
  }

  return {
    value: genderValue,
    label: gender
      ? !(gender === 'FEMALE' || gender === 'MALE' || gender === 'NOTTOSAY')
        ? gender
        : genders[gender]
      : ''
  };
};

export const calendarDateFormat = ({
  date,
  formatDate,
  timezone
}: {
  date: Date;
  formatDate: string;
  timezone?: string;
}) => (timezone ? formatInTimeZone(date, timezone, formatDate) : format(date, formatDate));

export const calendarDate = ({
  date,
  view,
  timezone,
  dateFormat = 'dd, MMMM yyyy'
}: {
  date: Date;
  view?: TCalendarView;
  timezone?: string;
  dateFormat?: string;
}) => {
  let formatDate = calendarDateFormat({ date, formatDate: dateFormat, timezone });
  let from = calendarDateFormat({ date, formatDate: 'yyyy-MM-dd 00:00:00', timezone });
  let to = calendarDateFormat({ date, formatDate: 'yyyy-MM-dd 23:59:59', timezone });

  if (view === 'dayGridMonth') {
    formatDate = `${calendarDateFormat({
      date: startOfMonth(date),
      formatDate: dateFormat,
      timezone
    })} - ${calendarDateFormat({ date: endOfMonth(date), formatDate: dateFormat, timezone })}`;

    from = calendarDateFormat({
      date: startOfMonth(date),
      formatDate: 'yyyy-MM-dd 00:00:00',
      timezone
    });
    to = calendarDateFormat({
      date: endOfMonth(date),
      formatDate: 'yyyy-MM-dd 23:59:59',
      timezone
    });
  }

  if (view === 'timeGridWeek') {
    formatDate = `${calendarDateFormat({
      date: startOfWeek(date, { weekStartsOn: 1 }),
      formatDate: dateFormat,
      timezone
    })} - ${calendarDateFormat({
      date: endOfWeek(date, { weekStartsOn: 1 }),
      formatDate: dateFormat,
      timezone
    })}`;

    from = calendarDateFormat({
      date: startOfWeek(date),
      formatDate: 'yyyy-MM-dd 00:00:00',
      timezone
    });
    to = calendarDateFormat({ date: endOfWeek(date), formatDate: 'yyyy-MM-dd 23:59:59', timezone });
  }

  return {
    formatDate,
    fromUtc: new Date(from).toISOString(),
    from,
    to,
    toUtc: new Date(to).toISOString()
  };
};

export const randomHexColor = () => `#${Math.floor(Math.random() * 16777215).toString(16)}`;

export const fileToObjectUrl = (image: File | string) =>
  image instanceof File ? URL.createObjectURL(image) : image;

export const toastError = (error: any) => {
  const message = Array.isArray(error?.response?.data?.message)
    ? error?.response?.data?.message.join(', ')
    : error?.response?.data?.message;

  toast({
    title: error?.response?.data?.error || 'Error',
    message,
    variant: 'error'
  });
};

export const downloadFile = (fileName: string, fileKey: string) =>
  saveAs(`${API_URL}/external/download?key=${fileKey}`, fileName);

export const fullName = (user: IPatientDetails | IDoctorDetails | null) =>
  user ? `${user?.profile?.first_name || '-'} ${user?.profile?.last_name || '-'}` : '-';

export const fullNameDoctor = (user: IDoctorResponse | null) =>
  user ? `${user?.first_name || '-'} ${user?.last_name || '-'}` : '-';

export const fullNamePatient = (user: IPatientResponse | null) =>
  user ? `${user?.first_name || '-'} ${user?.last_name || '-'}` : '-';

export const getAddress = (user: IPatientDetails | IDoctorDetails | null) =>
  user
    ? `${user?.profile?.street || '-'} ${user?.profile?.city || '-'}, ${
        user?.profile?.state || '-'
      }, ${user?.profile?.postcode || '-'}`
    : '-';

export const getAddressPatient = (user: IPatientResponse | null) =>
  user
    ? `${user?.street || '-'} ${user?.city || '-'}, ${user?.state || '-'}, ${user?.postcode || '-'}`
    : '-';

export const getGender = (gender: TGenders, genderOther: string) => {
  const genderLabel = GENDER_OPTIONS.find((item: ISelectOptions) => item.value === gender);
  return gender === 'OTHERS' ? genderOther : genderLabel?.label;
};

export const formatId = (value: number, size: number) => {
  let s = value + '';
  while (s.length < size) s = '0' + s;
  return s;
};

const shuffleArray = (array: any[]) => {
  for (var i = array.length - 1; i > 0; i--) {
    var j = Math.floor(Math.random() * (i + 1));
    var temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }
  return array;
};

export const generateRandomPword = (passwordLength?: number) => {
  passwordLength = passwordLength ? passwordLength : 12;

  const numberChars = '0123456789';
  const upperChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowerChars = 'abcdefghiklmnopqrstuvwxyz';
  const nonAlphaChars = '!@#$%^&*()_-+=[{]};:>|./?';
  const allChars = `${numberChars}${upperChars}${lowerChars}${nonAlphaChars}`;

  let randPasswordArray = Array(passwordLength);

  randPasswordArray[0] = numberChars;
  randPasswordArray[1] = upperChars;
  randPasswordArray[2] = lowerChars;
  randPasswordArray[3] = nonAlphaChars;
  randPasswordArray = randPasswordArray.fill(allChars, 4);

  return shuffleArray(
    randPasswordArray.map(function (x) {
      return x[Math.floor(Math.random() * x.length)];
    })
  ).join('');
};

export const fileChecker = ({
  file,
  fileTypes = [],
  fileSizeLimit
}: {
  file: File;
  fileTypes: string[];
  fileSizeLimit: number;
}) => {
  let response: IFileCheckResponse = {
    success: true,
    message: ''
  };

  if (file) {
    // @ts-ignore
    const ext = file.name.split('.').pop().toLowerCase();
    const validFileTypes = fileTypes;

    if (validFileTypes.includes(ext)) {
      const sizeLimit = fileSizeLimit / 1024;
      const sizeLimitByte = fileSizeLimit * 1000;
      const decimals = '';
      const k = 1024;
      const dm: number = decimals || 2;
      const sizes: string[] = ['Bytes', 'KB', 'MB'];
      const bigSizes: any = ['GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const i = Math.floor(Math.log(file.size) / Math.log(k));
      const iC = Math.floor(Math.log(sizeLimitByte) / Math.log(k));
      const fileSize = parseFloat((file.size / Math.pow(k, i)).toFixed(dm));

      if ((fileSize > sizeLimit && sizes[i] === 'MB') || bigSizes.includes(sizes[i], bigSizes)) {
        const fSize = sizes[i];
        const cSize = sizes[iC];
        const limit = iC > 1 ? Math.round(sizeLimit) : fileSizeLimit;
        response = {
          success: false,
          message: `${file.name} has ${fileSize}${fSize} of file size. Please limit file size to ${limit}${cSize} and under only.`
        };
      }
    } else {
      response = {
        success: false,
        message: `${file.name} is not a valid file. We only accept ${validFileTypes.join(', ')}.`
      };
    }
  }

  return response;
};

export const checkBooleanString = (value?: string) => ({
  stringValue: value
    ? ['true', 'false'].includes(value)
      ? JSON.parse(value)
        ? 'Yes'
        : 'No'
      : '-'
    : 'No',
  isTrue: value ? (['true', 'false'].includes(value) ? JSON.parse(value) : false) : false
});

export const checkBoolean = (value?: 1 | 2) => ({
  stringValue: value ? (value === 1 ? 'Yes' : 'No') : '-',
  isTrue: value ? (value === 1 ? true : false) : false
});

// export const getHolisticHealth = (healthHistory?: IPatientHistory) => ({
//   diet: healthHistory?.current_diet ? healthHistory?.current_diet : '-',
//   activityLevel: healthHistory?.physical_activity_level
//     ? healthHistory?.physical_activity_level
//     : '-',
//   emotions: healthHistory?.stress_emotions ? healthHistory?.stress_emotions : '-'
// });

// export const getMedicineDetails = (healthHistory?: IPatientHistory) => ({
//   withPrescription: healthHistory?.medicine_with_prescription_details
//     ? `Frequency: ${
//         (JSON.parse(healthHistory.medicine_with_prescription_details) as IMedicineDetails).frequency
//       }, Type: ${
//         (JSON.parse(healthHistory.medicine_with_prescription_details) as IMedicineDetails).type
//       }`
//     : '-',
//   withoutPrescription: healthHistory?.medicine_without_prescription_details
//     ? `Frequency: ${
//         (JSON.parse(healthHistory.medicine_without_prescription_details) as IMedicineDetails)
//           .frequency
//       }, Type: ${
//         (JSON.parse(healthHistory.medicine_without_prescription_details) as IMedicineDetails).type
//       }`
//     : '-'
// });

export const getHealthCondition = (healthHistory?: IPatientHistory) => ({
  diagnosed_conditions: healthHistory?.diagnosed_conditions
    ? JSON.parse(healthHistory.diagnosed_conditions).map((item: any) => {
        return item.value;
      })
    : ([] as string[]),
  medication_list: healthHistory?.medication_list
    ? JSON.parse(healthHistory.medication_list).map((item: any) => {
        return item.value;
      })
    : ([] as string[]),
  practitioner_contact_details: healthHistory?.practitioner_contact_details
    ? JSON.parse(healthHistory.practitioner_contact_details).map((item: any) => {
        return `<div>
             <span className="font-semibold">${item.name} - </span>
             <span>${item.specialty}</span>
            <span>${item.contact}</span>
        </div>`;
      })
    : ([] as string[])
});

export const getPatientBackground = (background?: IPatientBackground) => ({
  approach_your_health: background?.approach_your_health
    ? (background.approach_your_health as string)
    : '',
  diagnosed_health_condition: background?.diagnosed_health_condition
    ? (JSON.parse(background?.diagnosed_health_condition) as string[])
    : ([] as string[]),
  diagnosed_health_condition_others: background?.diagnosed_health_condition_others
    ? (background.diagnosed_health_condition_others as string)
    : '',
  gastro: background?.gastro ? (JSON.parse(background?.gastro) as string[]) : ([] as string[]),
  health_goals: background?.health_goals ? background?.health_goals : ([] as string[]),
  health_goals_others: background?.health_goals_others
    ? (background.health_goals_others as string)
    : '',
  hear_about: background?.hear_about ? (background.hear_about as string) : '',
  interested_speciality: background?.interested_speciality
    ? (background.interested_speciality as string)
    : '',
  menopausal: background?.menopausal
    ? (JSON.parse(background?.menopausal) as string[])
    : ([] as string[]),
  menstruation: background?.menstruation
    ? (JSON.parse(background?.menstruation) as string[])
    : ([] as string[]),
  mood: background?.mood ? (JSON.parse(background?.mood) as string[]) : ([] as string[]),
  other: background?.other ? (JSON.parse(background?.other) as string[]) : ([] as string[]),
  other_symptoms: background?.other_symptoms ? (background.other_symptoms as string) : '',
  pain: background?.pain ? (JSON.parse(background?.pain) as string[]) : ([] as string[]),
  sexual_health: background?.sexual_health
    ? (JSON.parse(background?.sexual_health) as string[])
    : ([] as string[]),
  undiagnosed_health_condition: background?.undiagnosed_health_condition
    ? (background.undiagnosed_health_condition as string)
    : '',
  vaginal_health: background?.vaginal_health
    ? (JSON.parse(background?.vaginal_health) as string[])
    : ([] as string[]),
  virtual_not_appropriate_reason: background?.virtual_not_appropriate_reason
    ? (background.virtual_not_appropriate_reason as string)
    : '',
  what_brings_you_to_hazel: background?.what_brings_you_to_hazel
    ? (background.what_brings_you_to_hazel as string)
    : '',
  whatelse_interested: background?.whatelse_interested
    ? (JSON.parse(background?.whatelse_interested) as string[])
    : ([] as string[]),
  whatelse_interested_others: background?.whatelse_interested_others
    ? (background.whatelse_interested_others as string)
    : '',
  experiencing_any_pain: background?.experiencing_any_pain
    ? background?.experiencing_any_pain
    : ([] as string[])
});

export const getPreExistingConditions = (healthHistory?: IPatientHistory) => ({
  any_other_condition_aware_of_auto_immunity:
    healthHistory?.any_other_condition_aware_of_auto_immunity
      ? (JSON.parse(healthHistory.any_other_condition_aware_of_auto_immunity) as string[])
      : ([] as string[]),
  any_other_condition_aware_of_cardio: healthHistory?.any_other_condition_aware_of_cardio
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_cardio) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_derma: healthHistory?.any_other_condition_aware_of_derma
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_derma) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_gastro: healthHistory?.any_other_condition_aware_of_gastro
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_gastro) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_mental: healthHistory?.any_other_condition_aware_of_mental
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_mental) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_metabolic: healthHistory?.any_other_condition_aware_of_metabolic
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_metabolic) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_muskolo: healthHistory?.any_other_condition_aware_of_muskolo
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_muskolo) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_nuero: healthHistory?.any_other_condition_aware_of_nuero
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_nuero) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_reproductive:
    healthHistory?.any_other_condition_aware_of_reproductive
      ? (JSON.parse(healthHistory.any_other_condition_aware_of_reproductive) as string[])
      : ([] as string[]),
  any_other_condition_aware_of_urinary: healthHistory?.any_other_condition_aware_of_urinary
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_urinary) as string[])
    : ([] as string[]),
  investigation_surgical_intervention: healthHistory?.investigation_surgical_intervention
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_urinary) as string[])
    : ([] as string[]),
  mens_blood_color: healthHistory?.mens_blood_color
    ? (JSON.parse(healthHistory.mens_blood_color) as string[])
    : ([] as string[]),
  mens_apperance: healthHistory?.mens_apperance
    ? (JSON.parse(healthHistory.mens_apperance) as string[])
    : ([] as string[]),
  investigation_surgical_surgeries: healthHistory?.investigation_surgical_surgeries
    ? (JSON.parse(healthHistory.investigation_surgical_surgeries) as string[])
    : ([] as string[]),
  natural_therapies_alternatives: healthHistory?.natural_therapies_alternatives
    ? JSON.parse(healthHistory?.natural_therapies_alternatives).map((item: any) => {
        if (typeof item === 'object') {
          return item.value;
        } else {
          return item;
        }
      })
    : ([] as string[])
});

export const getProfessionals = (professionals: string) => {
  let professionalsAns: string = '';
  switch (professionals) {
    case '0':
      professionalsAns = "I'm currently seeing healthcare professionals";
      break;
    case '1':
      professionalsAns =
        "I'm not currently seeing healthcare professionals, but I have in the past";
      break;
    case '2':
      professionalsAns =
        "I'm managing myself and have never seen healthcare professionals for these conditions/symptoms";
      break;
    default:
      break;
  }
  return professionalsAns;
};

export const getFrequencyMenstrual = (frequency: string) => {
  let patientFrequency: string = '';
  switch (frequency) {
    case '0':
      patientFrequency = 'Irregular cycle - shorter than 21 days, longer than 35 or inconsistent';
      break;
    case '1':
      patientFrequency = 'Regular cycle - every 22- 34 days';
      break;
    case '2':
      patientFrequency =
        'Amenorrhea -absence of more than 3 menstrual cycles after having regular periods';
      break;
    case '3':
      patientFrequency = 'Determined by contraception- my cycle is influenced by my contraception';
      break;
    case '4':
      patientFrequency = "I've never had a period";
      break;
    case '5':
      patientFrequency = 'I am no longer having periods';
      break;
    case '6':
      patientFrequency = "I'm not sure";
      break;
    default:
      break;
  }
  return patientFrequency;
};

export const getPeriodLast = (period: string) => {
  let periodLast: string = '';
  switch (period) {
    case '0':
      periodLast = '1-3 days';
      break;
    case '1':
      periodLast = '3-7 days';
      break;
    case '2':
      periodLast = 'More than 7 days';
      break;
    case '3':
      periodLast = "I'm not sure";
      break;
    default:
      break;
  }
  return periodLast;
};

export const getMentrualFlow = (flow: string) => {
  let mentrualFlow: string = '';
  switch (flow) {
    case '0':
      mentrualFlow = 'Heavy flow - changing sanitary products every 1 -2 hours';
      break;
    case '1':
      mentrualFlow = 'Moderate flow - changing sanitary products every 3 hours';
      break;
    case '2':
      mentrualFlow = 'Light flow - changing sanitary products every 4 - 5+ hours';
      break;
    case '3':
      mentrualFlow = 'Spotting - requiring only a sanitary liner or less to manage';
      break;
    case '4':
      mentrualFlow = 'Varies - combination of the above or varies across cycles';
      break;
    case '5':
      mentrualFlow = "I'm not sure";
      break;
    default:
      break;
  }
  return mentrualFlow;
};

export const getMenstrualPain = (pain: string) => {
  let painLevel: string = '';
  switch (pain) {
    case '0':
      painLevel = '0 No pain';
      break;
    case '1':
      painLevel = '1-3 Mild pain: noticeable but does not interfere with daily activities';
      break;
    case '2':
      painLevel = '4-6 Moderate pain: more noticeable and may interfere with daily activities';
      break;
    case '3':
      painLevel =
        '7-9 Severe pain: intense and limits daily activities, making it difficult to concentrate or sleep';
      break;
    case '4':
      painLevel = '10 Worst pain imaginable: unbearable and may cause nausea or vomiting';
      break;
    case '5':
      painLevel = "I'm not sure";
      break;
    default:
      break;
  }
  return painLevel;
};

export const getValueFromOption = (index: string, list: string[]) => {
  const indexNum = Number(index);
  const data = list[indexNum];

  return data;
};

export const getBooleanValue = (value: string) => {
  const answer: string = value === '1' ? 'Yes' : 'No';

  return answer;
};

export const photoPlaceholder: {
  patient: string;
  doctor: string;
} = {
  patient: PhotoPlaceholderPatient,
  doctor: PhotoPlaceholderDoctor
};

export const handleDownloadLink = (url: string) => {
  const link = document.createElement('a');
  link.href = url;
  // link.setAttribute('download');

  // Append to html link element page
  document.body.appendChild(link);

  // Start download
  link.click();

  // Clean up and remove the link
  if (link.parentNode) link.parentNode.removeChild(link);
};

export const capitalizeFirstLetter = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1);

export const validateMedicareNo = (medicareNo: string) => {
  let isValid = false;

  if (medicareNo && medicareNo.length === 11) {
    const medicare_no = medicareNo.substring(0, 10);
    const matches = medicare_no.match(/^(\d{8})(\d)/);

    if (!matches) {
      return false;
    }

    const base = matches[1];
    const checkDigit = matches[2];
    const weights = [1, 3, 7, 9, 1, 3, 7, 9];

    let sum = 0;
    for (let i = 0; i < weights.length; i++) {
      sum += parseInt(base[i], 10) * weights[i];
    }

    isValid = sum % 10 === parseInt(checkDigit, 10);
  }

  return isValid;
};

export const getOptionHealthHistory = (value: string | any, options: string[]) => {
  const index = parseInt(value, 10);

  if (Number.isInteger(index) && index >= 0 && index < options.length) {
    return options[index];
  }

  return false;
};

export const inboxStatus = (status: string) => {
  const itemStatus = status?.toLowerCase();
  switch (itemStatus) {
    case 'urgent':
      return {
        label: 'Urgent',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-red-500/70'
      };
    case 'non_urgent':
      return {
        label: 'Non-Urgent',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-[#FFDDB9]'
      };
    case 'high':
      return {
        label: 'High',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-red-500/70'
      };
    case 'medium':
      return {
        label: 'Medium',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-[#FFDDB9]'
      };
    case 'low':
      return {
        label: 'Low',
        textColor: 'text-hazel-solid-green-600',
        backgroundColor: 'bg-hazel-solid-green-600/20'
      };
    default:
      return {
        label: status || 'N/A',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-[#ecefe9]'
      };
  }
};

export const mapToOptions = (data: any[]) => {
  return data.map((item) => ({
    value: item.id,
    label: `${item.first_name} ${item.last_name}`
  }));
};

export const getInboxStatusLabel = (value: string) => {
  switch (value) {
    case 'pending':
      return 'bg-orange-600';
    case 'urgent':
      return 'bg-red-500';
    case 'follow-up':
      return 'bg-hazel-orange-100';
  }
};

export const getTagInboxStatusLabel = (value: string) => {
  switch (value) {
    case 'Results':
      return {
        textColor: 'text-[#1C274C]',
        backgroundColor: 'bg-[#0000FF]/20'
      };
    case 'Referral':
      return {
        textColor: 'text-[#1C274C]',
        backgroundColor: 'bg-[#0000FF]/20'
      };
    case 'ID Document':
      return {
        textColor: 'text-[#1C274C]',
        backgroundColor: 'bg-[#0000FF]/20'
      };
    case 'Letter':
      return {
        textColor: 'text-[#1C274C]',
        backgroundColor: 'bg-[#0000FF]/20'
      };
    case 'Normal':
      return {
        textColor: 'text-hazel-solid-green-600',
        backgroundColor: 'bg-hazel-solid-green-600/20'
      };
    case 'Abnormal':
      return {
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-red-500/70'
      };
    default:
      return {
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-[#ecefe9]'
      };
  }
};
